/* eslint-disable */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Button,
  Grid,
  Group,
  Select,
  Textarea,
  TextInput,
  NumberInput,
  Text,
  Stack,
  Badge,
  Alert,
  Box,
  Modal,
  SegmentedControl,
  ActionIcon,
  ScrollArea,
} from "@mantine/core";
import { IconAlertCircle, IconUpload, IconTrash } from "@tabler/icons-react";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
import { useForm, zodResolver } from "@mantine/form";
import { z } from "zod";
import { useUser } from "hooks/useUser";
import { toIsoWithOffset } from "utils/dateFormater";
import { useAddLeaveRequest } from "api/newLeave/leaveRequest";
import { useGetLeaveBalance } from "api/newLeave/getLeavebalance";
import { useHourlyLeaveRequest } from "api/newLeave/AddHoursLeave";
import { useAddMultipleLeaves } from "api/newLeave/addmultipleleave";
import { formatLeaveNumber } from "utils/formatLeaveNumber";
import { useGetLeaveConfiguration } from "api/leaveConfiguration/getLeaveConfig";
import { useUploadFile } from "api/upload/uploadFile";
// import { formatDate } from "utils/dateleaveFormat";

const today = new Date().toISOString().split("T")[0];
const MAX_REASON = 500;
const MEDICAL_MAX_SIZE = 5 * 1024 * 1024;
const ACCEPTED_FILE_TYPES = ["application/pdf", "image/jpeg", "image/png"];

export const addLeaveSchema = z.object({
  leaveType: z.string().trim().optional(),
  fromDate: z.string().trim().optional(),
  toDate: z.string().trim().optional(),
  reason: z.string().optional(),
  unpaidDays: z.number().optional(),
  medicalFile: z.any().optional(),
});

type AddHolidayFormProps = { onClose: () => void };

type MultipleLeaveRow = {
  id: string;
  leaveType: string;
  fromDate: string;
  toDate: string;
  reason?: string;
  convertToUnpaid?: boolean;
  unpaidDays?: number | undefined;
};

export default function AddLeaveRequestForm({ onClose }: AddHolidayFormProps) {
  const { user }: any = useUser();
  const { data } = useGetLeaveBalance();
  const { data: leaveConfigData } = useGetLeaveConfiguration(user?.org?._id);
  const { mutate: addMultipleLeaves } = useAddMultipleLeaves();
  const { mutate: addLeave, isLoading } = useAddLeaveRequest();
  const { mutate: createHourlyLeave } = useHourlyLeaveRequest();
  const { mutateAsync: uploadFile, isLoading: isUploading } = useUploadFile();
  const [leaveBalance, setLeaveBalance] = useState<
    Record<string, number | null>
  >({});
  const [orgHolidays, setOrgHolidays] = useState<string[]>([]);
  const [overlapChecking, setOverlapChecking] = useState(false);
  const [overlapError, setOverlapError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [advisory, setAdvisory] = useState<string | null>(null);

  const [confirmModalOpened, setConfirmModalOpened] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState<string | null>(null);
  const [pendingPayload, setPendingPayload] = useState<any>(null);
  const [pendingFormData, setPendingFormData] = useState<
    FormData | undefined
  >();

  const [mode, setMode] = useState<"days" | "hours" | "multiple">("days");

  const [multipleLeaves, setMultipleLeaves] = useState<MultipleLeaveRow[]>([
    {
      id: String(Date.now()),
      leaveType: "",
      fromDate: "",
      toDate: "",
      reason: "",
      convertToUnpaid: false,
    },
  ]);

  const [rowFiles, setRowFiles] = useState<Record<string, File | null>>({});

  const leaveTypeOptions = [
    { value: "annual", label: "Annual Leave" },
    { value: "sick", label: "Sick Leave" },
    { value: "pressingNecessity", label: "Pressing Necessity" },
    { value: "compassionate", label: "Compassionate Leave" },
    { value: "bereavement", label: "Bereavement Leave" },
    { value: "jury_duty", label: "Jury Duty" },
    { value: "election", label: "Election Leave" },
    { value: "band_business", label: "Band Business Leave" },
    { value: "governance", label: "Governance Leave" },
    { value: "education", label: "Education Leave" },
    { value: "professionalDevelopment", label: "Professional Development" },
    { value: "culturalSpiritual", label: "Cultural & Spiritual Leave" },
    { value: "leaveWithoutPay", label: "Leave Without Pay (LWP)" },
    { value: "otherLeave", label: "Other" },
  ];

  const form = useForm({
    initialValues: {
      leaveType: "",
      fromDate: "",
      toDate: "",
      reason: "",
      unpaidDays: undefined as number | undefined,
      certificateDocument: undefined as File | undefined,
      departmentId: user?.department?._id,
      hours: undefined as number | undefined,
    },
    validate: zodResolver(addLeaveSchema),
  });

  useEffect(() => {
    if (mode === "hours") {
      if (form.values.fromDate) {
        form.setFieldValue("toDate", form.values.fromDate);
      }
    }
  }, [mode, form.values.fromDate, form]);

  async function fetchLeaveBalanceApi(type: string) {
    const defaults: Record<string, number> = {
      annual: 0,
      sick: 0,
      pressing_necessity: 0,
      education: 0,
    };

    try {
      const api = (data && (data as any).data) ?? null;
      if (api) {
        const map: Record<string, number | null> = {
          annual:
            (api.annualLeave?.balance ??
            api.annualLeave?.allocated ??
            api.annualLeave?.used != null)
              ? Math.max(
                  0,
                  (api.annualLeave?.allocated ?? 0) -
                    (api.annualLeave?.used ?? 0),
                )
              : null,
          sick: api.sickLeave?.balance ?? api.sickLeave?.accrued ?? null,
          pressing_necessity:
            api.pressingNecessity?.balance ??
            api.pressingNecessity?.allocated ??
            null,
          compassionate: api.compassionate?.balance ?? null,
          bereavement: api.bereavement?.balance ?? null,
          jury_duty: api.juryDuty?.balance ?? null,
          election: api.election?.balance ?? null,
          band_business: api.bandBusiness?.balance ?? null,
          governance: api.governance?.balance ?? null,
          education: api.education?.balance ?? null,
          professional_development:
            api.professionalDevelopment?.balance ?? null,
          cultural_spiritual: api.culturalSpiritual?.balance ?? null,
          leave_without_pay: null,
          other: null,
        };
        if (map[type] != null) return map[type];
      }
    } catch (err) {}
    return defaults[type] ?? null;
  }

  useEffect(() => {
    if (!data || !(data as any).data) return;
    const api = (data as any).data;
    const mapped: Record<string, number | null> = {
      annual: api.annualLeave?.balance ?? api.annualLeave?.allocated ?? null,
      sick: api.sickLeave?.balance ?? api.sickLeave?.accrued ?? null,
      pressing_necessity:
        api.pressingNecessity?.balance ??
        api.pressingNecessity?.allocated ??
        null,
      compassionate: api.compassionate?.balance ?? null,
      bereavement: api.bereavement?.balance ?? null,
      jury_duty: api.juryDuty?.balance ?? null,
      election: api.election?.balance ?? null,
      band_business: api.bandBusiness?.balance ?? null,
      governance: api.governance?.balance ?? null,
      education: api.education?.balance ?? null,
      professional_development: api.professionalDevelopment?.balance ?? null,
      cultural_spiritual: api.culturalSpiritual?.balance ?? null,
      leave_without_pay: null,
      other: null,
    };
    setLeaveBalance((prev) => ({ ...prev, ...mapped }));
  }, [data]);

  async function checkOverlappingLeavesApi(
    userId: string | undefined,
    fromIso: string,
    toIso: string,
  ) {
    return { hasOverlap: false, message: "" };
  }

  function businessDaysBetween(
    startISO: string,
    endISO: string,
    holidays: string[],
  ) {
    if (!startISO || !endISO) return 0;
    const start = new Date(startISO);
    const end = new Date(endISO);
    if (end < start) return 0;
    let count = 0;
    const holidaySet = new Set(holidays.map((d) => new Date(d).toDateString()));
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const day = d.getDay();
      if (day === 0 || day === 6) continue;
      if (holidaySet.has(new Date(d).toDateString())) continue;
      count++;
    }
    return count;
  }

  useEffect(() => {
    let mounted = true;
    const t = form.values.leaveType;
    if (!t) return;
    (async () => {
      try {
        const remaining = await fetchLeaveBalanceApi(t);
        if (!mounted) return;
        setLeaveBalance((prev) => ({ ...prev, [t]: remaining }));
      } catch (err) {}
    })();
    return () => {
      mounted = false;
    };
  }, [form.values.leaveType, data]);

  const requestedDays = useMemo(() => {
    return businessDaysBetween(
      form.values.fromDate,
      form.values.toDate,
      orgHolidays,
    );
  }, [form.values.fromDate, form.values.toDate, orgHolidays]);

  // Get certificate requirements from configuration
  const sickLeaveConfig = leaveConfigData?.data?.sickLeave;
  const certificateRequired = useMemo(() => {
    if (form.values.leaveType !== "sick") return false;
    if (!sickLeaveConfig) return requestedDays > 3; // Fallback to default
    if (!sickLeaveConfig.requireCertification) return false;
    return requestedDays > (sickLeaveConfig.certificationAfterDays || 3);
  }, [form.values.leaveType, requestedDays, sickLeaveConfig]);

  useEffect(() => {
    const type = form.values.leaveType;
    const balance = type ? (leaveBalance[type] ?? null) : null;
    setAdvisory(null);
    if (
      type === "annual" &&
      balance != null &&
      balance > 0 &&
      requestedDays > 0
    ) {
      const percent = (requestedDays / (balance || 1)) * 100;
      if (percent >= 60) {
        setAdvisory(
          `${Math.round(
            percent,
          )}% of your annual leave allocation and may require director approval.`,
        );
      }
    }
  }, [form.values.leaveType, leaveBalance, requestedDays]);

  useEffect(() => {
    if (form.values.leaveType === "leave_without_pay") {
      form.setFieldValue("unpaidDays", requestedDays || undefined);
    }
  }, [form.values.leaveType, requestedDays]);

  const handleRowFileSelect = useCallback((id: string, file: File | null) => {
    setFileError(null);
    setRowFiles((prev) => ({ ...prev, [id]: file }));
  }, []);

  const handleFileSelect = useCallback((file: File | null) => {
    setFileError(null);
    if (!file) return;
    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      setFileError("Only PDF, JPG, PNG files are accepted.");
      return;
    }
    if (file.size > MEDICAL_MAX_SIZE) {
      setFileError("File size must be less than 5 MB.");
      return;
    }
  }, []);

  const handleAddLeaveRequest = useCallback(
    (payload: any, formData?: FormData) => {
      addLeave(formData ?? payload, {
        onSuccess: () => onClose(),
        onError: (err: any) => {
          const status = err?.response?.status ?? err?.status ?? null;
          if (status === 400) {
            const message =
              err?.response?.data?.message ||
              err?.response?.data?.error ||
              err?.data?.message ||
              "Request failed with status 400. Do you want to convert this request to unpaid?";

            setConfirmMessage(message);
            setPendingPayload(payload);
            setPendingFormData(formData);
            setConfirmModalOpened(true);
          }
        },
      });
    },
    [addLeave, onClose],
  );

  const handleAddMultipleLeaves = useCallback(
    (payload: any) => {
      addMultipleLeaves(payload, {
        onSuccess: () => onClose(),
        onError: (err: any) => {
          const status = err?.response?.status ?? err?.status ?? null;
          if (status === 400) {
            const message =
              err?.response?.data?.message ||
              err?.response?.data?.error ||
              err?.data?.message ||
              "Request failed with status 400. Do you want to convert this request to unpaid?";

            setConfirmMessage(message);
            setPendingPayload(payload);
            setConfirmModalOpened(true);
          }
        },
      });
    },
    [addMultipleLeaves, onClose],
  );

  const handleSubmit = async (values: typeof form.values) => {
    setFileError(null);
    setOverlapError(null);
    form.clearErrors();

    if (mode === "hours") {
      if (!values.fromDate) {
        form.setFieldError("fromDate", "Please select a date for hourly leave");
        return;
      }
      const hours = values.hours;
      if (hours === undefined || hours === null || hours <= 0) {
        form.setFieldError("hours", "Please enter number of hours (> 0)");
        return;
      }
      if (hours > 24) {
        form.setFieldError("hours", "Hours cannot exceed 24");
        return;
      }

      setOverlapChecking(true);
      try {
        const dateIso = toIsoWithOffset(values.fromDate);
        const res: any = await checkOverlappingLeavesApi(
          user?.id,
          dateIso,
          dateIso,
        );
        if (res.hasOverlap) {
          setOverlapError(
            res.message || "Selected date overlaps with existing leave.",
          );
          setOverlapChecking(false);
          return;
        }
      } catch (err) {
        setOverlapError("Could not validate date overlap. Please try again.");
        setOverlapChecking(false);
        return;
      }
      setOverlapChecking(false);

      const payload: any = {
        leaveType: values.leaveType,
        reason: values.reason,
        date: toIsoWithOffset(values.fromDate),
        hours: values.hours,
        departmentId: user?.department?._id,
        convertToUnpaid: false,
      };

      createHourlyLeave(payload, {
        onSuccess: () => onClose(),
        onError: (err: any) => {
          const status = err?.response?.status ?? err?.status ?? null;
          if (status === 400) {
            const message =
              err?.response?.data?.message ||
              err?.response?.data?.error ||
              err?.data?.message ||
              "Request failed with status 400.";
            setConfirmMessage(message);
            setPendingPayload(payload);
            setConfirmModalOpened(true);
          } else {
            setOverlapError(err?.message || "Hourly leave request failed.");
          }
        },
      });
      return;
    }

    if (mode === "multiple") {
      for (let i = 0; i < multipleLeaves.length; i++) {
        const r = multipleLeaves[i];
        if (!r.leaveType) {
          setOverlapError(`Please select leave type for row ${i + 1}`);
          return;
        }
        if (!r.fromDate || !r.toDate) {
          setOverlapError(
            `Please select both from and to dates for row ${i + 1}`,
          );
          return;
        }
        if (new Date(r.fromDate) > new Date(r.toDate)) {
          setOverlapError(`From date cannot be after To date for row ${i + 1}`);
          return;
        }
        const days = businessDaysBetween(r.fromDate, r.toDate, orgHolidays);
        // Check certificate requirement using configuration
        const threshold = sickLeaveConfig?.certificationAfterDays || 3;
        const needsCert =
          r.leaveType === "sick" &&
          sickLeaveConfig?.requireCertification !== false &&
          days > threshold;
        if (needsCert) {
          const file = rowFiles[r.id];
          if (!file) {
            setOverlapError(
              `Medical certificate required for sick leave > ${threshold} business days (row ${
                i + 1
              }).`,
            );
            return;
          }
        }
      }

      setOverlapChecking(true);
      try {
        for (let r of multipleLeaves) {
          const fromIso = toIsoWithOffset(r.fromDate);
          const toIso = toIsoWithOffset(r.toDate);
          const res: any = await checkOverlappingLeavesApi(
            user?.id,
            fromIso,
            toIso,
          );
          if (res.hasOverlap) {
            setOverlapError(
              res.message ||
                `Row with dates ${r.fromDate} - ${r.toDate} overlaps existing leave.`,
            );
            setOverlapChecking(false);
            return;
          }
        }
      } catch (err) {
        setOverlapError("Could not validate date overlap. Please try again.");
        setOverlapChecking(false);
        return;
      }
      setOverlapChecking(false);

      const leavesPayload: any[] = [];
      for (let r of multipleLeaves) {
        const leaveObj: any = {
          fromDate: toIsoWithOffset(r.fromDate),
          toDate: toIsoWithOffset(r.toDate),
          reason: r.reason,
          leaveType: r.leaveType,
          convertToUnpaid: !!r.convertToUnpaid,
        };
        if (r.leaveType === "leave_without_pay") {
          const days = businessDaysBetween(r.fromDate, r.toDate, orgHolidays);
          leaveObj.unpaidDays = r.unpaidDays ?? days;
        }

        const file = rowFiles[r.id];
        if (file) {
          try {
            // Upload certificate to S3 first
            const uploadResponse = await uploadFile(file);

            // Store the S3 URL instead of base64
            leaveObj.certificateDocument = uploadResponse.data.url;
            leaveObj.certificateDocumentName = file.name;
            leaveObj.isCertified = true;
          } catch (e) {
            setFileError(
              "Could not upload certificate for one of the rows. Please try again.",
            );
            return;
          }
        }

        leavesPayload.push(leaveObj);
      }

      const payload: any = {
        departmentId: user?.department?._id,
        leaves: leavesPayload,
      };

      handleAddMultipleLeaves(payload);
      return;
    }

    if (!values.fromDate || !values.toDate) {
      form.setFieldError("fromDate", "Please select date range");
      return;
    }

    const days = requestedDays;

    // Check certificate requirement using configuration
    if (certificateRequired) {
      if (!form.values.certificateDocument) {
        const threshold = sickLeaveConfig?.certificationAfterDays || 3;
        setFileError(
          `Medical certificate is required for sick leave longer than ${threshold} business days.`,
        );
        return;
      }
    }

    if (values.leaveType === "leave_without_pay") {
      if (
        (values.unpaidDays === undefined || values.unpaidDays === null) &&
        days >= 0
      ) {
        form.setFieldValue("unpaidDays", days);
      }
      if (values.unpaidDays === undefined || values.unpaidDays === null) {
        form.setFieldError("unpaidDays", "Please enter number of unpaid days");
        return;
      }
    }

    setOverlapChecking(true);
    try {
      const fromIso = toIsoWithOffset(values.fromDate);
      const toIso = toIsoWithOffset(values.toDate);
      const res: any = await checkOverlappingLeavesApi(
        user?.id,
        fromIso,
        toIso,
      );
      if (res.hasOverlap) {
        setOverlapError(
          res.message || "Selected dates overlap with an existing leave.",
        );
        setOverlapChecking(false);
        return;
      }
    } catch (err) {
      setOverlapError("Could not validate date overlap. Please try again.");
      setOverlapChecking(false);
      return;
    }
    setOverlapChecking(false);

    const payload: any = {
      leaveType: values.leaveType,
      fromDate: toIsoWithOffset(values.fromDate),
      toDate: toIsoWithOffset(values.toDate),
      reason: values.reason,
      requestedDays: days,
      departmentId: user?.department?._id,
      convertToUnpaid: false,
      certificateDocument: form.values.certificateDocument
        ? form.values.certificateDocument?.name
        : undefined,
    };

    if (values.leaveType === "leave_without_pay") {
      payload.unpaidDays = values.unpaidDays ?? days;
    }

    if (form.values.certificateDocument) {
      try {
        // Upload certificate to S3 first
        const uploadResponse = await uploadFile(
          form.values.certificateDocument,
        );

        // Store the S3 URL instead of base64
        payload.certificateDocument = uploadResponse.data.url;
        payload.certificateDocumentName = form.values.certificateDocument.name;
        payload.isCertified = true;

        handleAddLeaveRequest(payload);
        return;
      } catch (err) {
        setFileError("Could not upload the certificate. Please try again.");
        return;
      }
    }

    handleAddLeaveRequest(payload);
  };

  const addMultipleRow = () => {
    setMultipleLeaves((prev) => [
      ...prev,
      {
        id: String(Date.now()) + Math.random(),
        leaveType: "",
        fromDate: "",
        toDate: "",
        reason: "",
        convertToUnpaid: false,
      },
    ]);
  };

  const removeMultipleRow = (id: string) => {
    setMultipleLeaves((prev) => prev.filter((r) => r.id !== id));
    setRowFiles((prev) => {
      const cloned = { ...prev };
      delete cloned[id];
      return cloned;
    });
  };

  const updateMultipleRow = (id: string, patch: Partial<MultipleLeaveRow>) => {
    setMultipleLeaves((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    );
  };
  const remaining = (() => {
    if (!form.values.leaveType) return null;
    const lb = Number(leaveBalance[form.values.leaveType] ?? 0);
    const req = Number(requestedDays ?? 0);
    const rem = lb - req;
    return Number.isFinite(rem) ? rem.toFixed(2) : null;
  })();

  return (
    <>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Grid>
          <Grid.Col span={12}>
            <Group position="apart" align="center" mb="sm">
              <SegmentedControl
                value={mode}
                onChange={(val: "days" | "hours" | "multiple") => setMode(val)}
                data={[
                  { label: "Days", value: "days" },
                  { label: "Hours", value: "hours" },
                  { label: "Multiple", value: "multiple" },
                ]}
                size="md"
              />
            </Group>

            {mode !== "multiple" ? (
              <Select
                withAsterisk
                label="Leave Type"
                placeholder="Pick a leave type"
                data={leaveTypeOptions.map((opt) => {
                  const r = formatLeaveNumber(leaveBalance[opt.value]);
                  const suffix = r != null ? ` (${r} days)` : "";
                  return { value: opt.value, label: `${opt.label}${suffix}` };
                })}
                {...form.getInputProps("leaveType")}
              />
            ) : (
              <Text size="sm" mb="xs">
                Select leave types and date ranges for each row below.
              </Text>
            )}
          </Grid.Col>

          {mode === "hours" ? (
            <>
              <Grid.Col span={6}>
                <TextInput
                  withAsterisk
                  min={today}
                  type="date"
                  label="Date"
                  {...form.getInputProps("fromDate")}
                />
              </Grid.Col>

              <Grid.Col span={6}>
                <NumberInput
                  withAsterisk
                  label="Hours"
                  value={form.values.hours}
                  onChange={(v) => form.setFieldValue("hours", v ?? undefined)}
                  min={0.25}
                  step={0.25}
                  max={24}
                />
              </Grid.Col>

              {/* Reason for hours mode */}
              <Grid.Col span={12}>
                <Textarea
                  label="Reason"
                  placeholder="Explain the reason for this hourly leave (optional)"
                  maxLength={MAX_REASON}
                  {...form.getInputProps("reason")}
                />
                <Text size="xs" align="right">
                  {form.values.reason?.length ?? 0}/{MAX_REASON}
                </Text>
              </Grid.Col>
            </>
          ) : mode === "multiple" ? (
            <>
              {" "}
              <Grid.Col
                span={12}
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginBottom: 8,
                }}
              >
                <Button variant="outline" onClick={addMultipleRow}>
                  Add another leave
                </Button>
              </Grid.Col>
              <ScrollArea style={{ height: 360, width: "100%" }}>
                {multipleLeaves.map((row, idx) => (
                  <React.Fragment key={row.id}>
                    <Grid.Col span={12}>
                      <Box
                        p="sm"
                        sx={{ border: "1px solid #e9ecef", borderRadius: 6 }}
                      >
                        <Group position="apart">
                          <Text>
                            <strong>Row {idx + 1}</strong>
                          </Text>
                          <Group>
                            <ActionIcon
                              color="red"
                              onClick={() => removeMultipleRow(row.id)}
                              title="Remove row"
                            >
                              <IconTrash />
                            </ActionIcon>
                          </Group>
                        </Group>

                        <Grid mt="xs">
                          <Grid.Col span={4}>
                            <Select
                              withAsterisk
                              label="Leave Type"
                              placeholder="Pick a leave type"
                              data={leaveTypeOptions.map((opt) => {
                                const r = leaveBalance[opt.value];
                                const suffix = r != null ? ` (${r} days)` : "";
                                return {
                                  value: opt.value,
                                  label: `${opt.label}${suffix}`,
                                };
                              })}
                              value={row.leaveType}
                              onChange={(v) =>
                                updateMultipleRow(row.id, {
                                  leaveType: v ?? "",
                                })
                              }
                            />
                          </Grid.Col>

                          <Grid.Col span={4}>
                            <TextInput
                              withAsterisk
                              type="date"
                              label="From Date"
                              value={row.fromDate}
                              onChange={(e) =>
                                updateMultipleRow(row.id, {
                                  fromDate: e.target.value,
                                })
                              }
                            />
                          </Grid.Col>

                          <Grid.Col span={4}>
                            <TextInput
                              withAsterisk
                              type="date"
                              label="To Date"
                              value={row.toDate}
                              onChange={(e) =>
                                updateMultipleRow(row.id, {
                                  toDate: e.target.value,
                                })
                              }
                            />
                          </Grid.Col>

                          <Grid.Col span={12}>
                            <Textarea
                              label="Reason"
                              placeholder="Explain the reason for this leave"
                              maxLength={MAX_REASON}
                              value={row.reason}
                              onChange={(e) =>
                                updateMultipleRow(row.id, {
                                  reason: e.target.value,
                                })
                              }
                            />
                            <Text size="xs" align="right">
                              {row.reason?.length ?? 0}/{MAX_REASON}
                            </Text>
                          </Grid.Col>

                          {row.leaveType === "leave_without_pay" && (
                            <Grid.Col span={6}>
                              <NumberInput
                                label="Number of Unpaid Days"
                                description={`Auto-suggested: ${
                                  row.fromDate && row.toDate
                                    ? businessDaysBetween(
                                        row.fromDate,
                                        row.toDate,
                                        orgHolidays,
                                      )
                                    : 0
                                } business days`}
                                value={row.unpaidDays}
                                onChange={(v) =>
                                  updateMultipleRow(row.id, {
                                    unpaidDays: v ?? undefined,
                                  })
                                }
                                min={0}
                              />
                            </Grid.Col>
                          )}

                          {row.leaveType === "sick" &&
                            row.fromDate &&
                            row.toDate &&
                            sickLeaveConfig?.requireCertification !== false &&
                            businessDaysBetween(
                              row.fromDate,
                              row.toDate,
                              orgHolidays,
                            ) >
                              (sickLeaveConfig?.certificationAfterDays ||
                                3) && (
                              <Grid.Col span={12}>
                                <Text size="sm" mb="xs">
                                  Medical Certificate (required for sick leave
                                  &gt;{" "}
                                  {sickLeaveConfig?.certificationAfterDays || 3}{" "}
                                  business days)
                                </Text>
                                <Dropzone
                                  onDrop={(files: any) =>
                                    handleRowFileSelect(
                                      row.id,
                                      files[0] ?? null,
                                    )
                                  }
                                  onReject={(files: any) => {
                                    const first = files[0];
                                    if (!first) return;
                                    if (first.file.size > MEDICAL_MAX_SIZE) {
                                      setFileError(
                                        "File size must be less than 5 MB.",
                                      );
                                    } else {
                                      setFileError(
                                        "Only PDF, JPG, PNG files are accepted.",
                                      );
                                    }
                                  }}
                                  maxSize={MEDICAL_MAX_SIZE}
                                  accept={[
                                    MIME_TYPES.pdf,
                                    MIME_TYPES.png,
                                    MIME_TYPES.jpeg,
                                  ]}
                                  multiple={false}
                                  styles={(theme: any) => ({
                                    root: { padding: 10 },
                                  })}
                                >
                                  <Group
                                    position="center"
                                    spacing="sm"
                                    style={{ minHeight: 60 }}
                                  >
                                    <IconUpload size={20} />
                                    <div>
                                      <Text size="sm">
                                        Drag & drop file here or click to browse
                                      </Text>
                                      <Text size="xs">
                                        Allowed: PDF, JPG, PNG — max 5 MB
                                      </Text>
                                    </div>
                                  </Group>
                                </Dropzone>

                                <Group mt="xs">
                                  {rowFiles[row.id] && (
                                    <Badge>{rowFiles[row.id]?.name}</Badge>
                                  )}
                                  {fileError && (
                                    <Text color="red">{fileError}</Text>
                                  )}
                                </Group>
                              </Grid.Col>
                            )}
                        </Grid>
                      </Box>
                    </Grid.Col>

                    {/* <Grid.Col
                      span={1}
                      style={{
                        display: "flex",
                        alignItems: "start",
                        justifyContent: "center",
                      }}
                    >
                      <ActionIcon
                        color="red"
                        onClick={() => removeMultipleRow(row.id)}
                        title="Remove row"
                      >
                        <IconTrash />
                      </ActionIcon>
                    </Grid.Col> */}
                  </React.Fragment>
                ))}
              </ScrollArea>
              {/* <Grid.Col span={12}>
                <Button variant="outline" onClick={addMultipleRow}>
                  Add another leave
                </Button>
              </Grid.Col> */}
            </>
          ) : (
            <>
              <Grid.Col span={6}>
                <TextInput
                  withAsterisk
                  type="date"
                  label="From Date"
                  {...form.getInputProps("fromDate")}
                />
              </Grid.Col>

              <Grid.Col span={6}>
                <TextInput
                  withAsterisk
                  type="date"
                  label="To Date"
                  {...form.getInputProps("toDate")}
                />
              </Grid.Col>

              <Grid.Col span={12}>
                <Textarea
                  label="Reason"
                  placeholder="Explain the reason for this leave (optional)"
                  maxLength={MAX_REASON}
                  {...form.getInputProps("reason")}
                />
                <Text size="xs" align="right">
                  {form.values.reason?.length ?? 0}/{MAX_REASON}
                </Text>
              </Grid.Col>
            </>
          )}

          <Grid.Col span={12}>
            {mode === "hours" ? (
              <Stack spacing={6}>
                <Text size="sm">
                  Requested Hours: <strong>{form.values.hours ?? ""}</strong>
                </Text>
                <Text size="xs">
                  This request will be sent as an hours-based leave.
                </Text>
              </Stack>
            ) : mode === "multiple" ? (
              <Stack spacing={6}>
                <Text size="sm">
                  Multiple leave rows will be submitted together as one request.
                </Text>
              </Stack>
            ) : (
              <Stack spacing={6}>
                <Text size="sm">
                  Business Days: <strong>{requestedDays}</strong>{" "}
                  {requestedDays > 0 && (
                    <span>(excluding weekends & holidays)</span>
                  )}
                </Text>
                {orgHolidays.length > 0 && (
                  <Text size="xs">
                    Organization holidays are applied to the calculation.
                  </Text>
                )}
              </Stack>
            )}
          </Grid.Col>

          {form.values.leaveType === "leave_without_pay" && mode === "days" && (
            <Grid.Col span={12}>
              <NumberInput
                withAsterisk
                label="Number of Unpaid Days"
                description={`Auto-suggested: ${requestedDays} business days`}
                value={form.values.unpaidDays}
                onChange={(v) =>
                  form.setFieldValue("unpaidDays", v ?? undefined)
                }
                min={0}
              />
            </Grid.Col>
          )}

          {certificateRequired && mode === "days" && (
            <Grid.Col span={12}>
              <Text size="sm" mb="xs">
                Medical Certificate (required for sick leave &gt;{" "}
                {sickLeaveConfig?.certificationAfterDays || 3} business days)
              </Text>

              <Dropzone
                onDrop={(files: any) => {
                  const first = files[0] ?? null;
                  if (first) {
                    if (!ACCEPTED_FILE_TYPES.includes(first.type)) {
                      setFileError("Only PDF, JPG, PNG files are accepted.");
                      return;
                    }
                    if (first.size > MEDICAL_MAX_SIZE) {
                      setFileError("File size must be less than 5 MB.");
                      return;
                    }
                    form.setFieldValue("certificateDocument", first);
                  }
                }}
                onReject={(files: any) => {
                  const first = files[0];
                  if (!first) return;
                  if (first.file.size > MEDICAL_MAX_SIZE) {
                    setFileError("File size must be less than 5 MB.");
                  } else {
                    setFileError("Only PDF, JPG, PNG files are accepted.");
                  }
                }}
                maxSize={MEDICAL_MAX_SIZE}
                accept={[MIME_TYPES.pdf, MIME_TYPES.png, MIME_TYPES.jpeg]}
                multiple={false}
                styles={(theme: any) => ({ root: { padding: 10 } })}
              >
                <Group position="center" spacing="sm" style={{ minHeight: 60 }}>
                  <IconUpload size={20} />
                  <div>
                    <Text size="sm">
                      Drag & drop file here or click to browse
                    </Text>
                    <Text size="xs">Allowed: PDF, JPG, PNG — max 5 MB</Text>
                  </div>
                </Group>
              </Dropzone>

              <Group mt="xs">
                {form.values.certificateDocument && (
                  <Badge>{form.values.certificateDocument.name}</Badge>
                )}
                {fileError && <Text color="red">{fileError}</Text>}
              </Group>
            </Grid.Col>
          )}

          <Grid.Col span={12}>
            <Stack spacing={6}>
              {overlapError && (
                <Alert title="Date overlap" color="red">
                  {overlapError}
                </Alert>
              )}
              {advisory && (
                <Alert
                  icon={<IconAlertCircle />}
                  title="Advisory"
                  color="yellow"
                >
                  {advisory}
                </Alert>
              )}
            </Stack>
          </Grid.Col>

          <Grid.Col span={12}>
            <Box p="sm" sx={{ border: "1px solid #e9ecef", borderRadius: 6 }}>
              <Group position="apart">
                <Text>
                  <strong>Current Balance</strong>
                </Text>
                <Text>
                  {form.values.leaveType
                    ? leaveBalance[form.values.leaveType] != null
                      ? `${leaveBalance[form.values.leaveType]?.toFixed(
                          2,
                        )} days`
                      : "0"
                    : "0"}
                </Text>
              </Group>

              {mode === "days" && (
                <>
                  <Group position="apart">
                    <Text>
                      <strong>Requested</strong>
                    </Text>
                    <Text>{requestedDays} days</Text>
                  </Group>
                  <Group position="apart">
                    <Text>
                      <strong>Remaining</strong>
                    </Text>
                    <Text>
                      {remaining !== null ? `${remaining} days` : "0"}
                    </Text>
                  </Group>
                </>
              )}

              {mode === "hours" && (
                <Group position="apart">
                  <Text>
                    <strong>Requested (hours)</strong>
                  </Text>
                  <Text>{form.values.hours ?? "0"}</Text>
                </Group>
              )}
            </Box>
          </Grid.Col>
        </Grid>

        <Group position="right" spacing={20} mt={20}>
          <Button color="cancel" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="filled"
            loading={isLoading || overlapChecking || isUploading}
          >
            Submit Request
          </Button>
        </Group>
      </form>

      <Modal
        opened={confirmModalOpened}
        onClose={() => setConfirmModalOpened(false)}
        title="Convert to Unpaid?"
        centered
      >
        <Text mb="md">{confirmMessage}</Text>
        <Group position="right">
          <Button
            variant="outline"
            onClick={() => setConfirmModalOpened(false)}
          >
            No
          </Button>
          <Button
            onClick={() => {
              if (pendingPayload) {
                if (Array.isArray(pendingPayload.leaves)) {
                  pendingPayload.leaves = pendingPayload.leaves.map(
                    (l: any) => ({
                      ...l,
                      convertToUnpaid: true,
                    }),
                  );
                  handleAddMultipleLeaves(pendingPayload);
                } else {
                  if (mode === "hours") {
                    pendingPayload.convertToUnpaid = true;
                    createHourlyLeave(pendingPayload, { onSuccess: onClose });
                  } else {
                    pendingPayload.convertToUnpaid = true;
                    if (pendingFormData) {
                      pendingFormData.set(
                        "payload",
                        JSON.stringify(pendingPayload),
                      );
                      addLeave(pendingFormData as any, { onSuccess: onClose });
                    } else {
                      addLeave(pendingPayload, { onSuccess: onClose });
                    }
                  }
                }
              }
              setConfirmModalOpened(false);
            }}
          >
            Yes
          </Button>
        </Group>
      </Modal>
    </>
  );
}
